'use client'

import type React from 'react'
import { useEffect, useMemo, useRef, useState } from 'react'

import { styled } from 'styled-components'

import {
  AnalyticsEntityType,
  AnalyticsSource,
  FormattedMessage,
  Link,
  NestableAnalyticsProvider,
} from '@b-stock/fe-core/bstock-react'
import { Breakpoints, Colors, Typography } from '@b-stock/fe-core/design-system'

import AuctionCardSkeleton from '@/components/common/auctionCard/AuctionCardSkeleton'
import { ItemListProvider } from '@/helpers/telemetry/itemListContext'
import { useAnalytics } from '@/helpers/telemetry/SegmentAnalytics'
import { useViewItemListOnVisible } from '@/helpers/telemetry/useViewItemListOnVisible'
import { useAuctionQuery } from '@/helpers/useAuctionQuery'

import AuctionCard from '../common/auctionCard/AuctionCard'
import Carousel from '../common/carousel/Carousel'
import {
  Container,
  Header,
  ViewAllButton,
  ViewAllButtonMobile,
} from '../common/carousel/ui'
import ListingCardCarousel from './ListingCardCarousel'

const Subheader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0 0.125rem;
`

const SubheaderLink = styled(Link)`
  ${Typography.Body2Semibold};
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  width: fit-content;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`

const Title = styled.h2`
  ${Typography.Title3};
  color: ${Colors.Semantic.Neutral.light_black};
  padding: 0 0.125rem;

  @media ${Breakpoints.min.medium} {
    ${Typography.Title2};
  }
`

export interface ShopMarketplaceCarouselProps {
  title: React.ReactNode
  carousel_name?: string
  screenName?: string
  viewMoreUrl?: string
  endpoint: string | string[]
  minItemsToShowButtons?: number
  carousel_position?: number
  /** Every listing in this carousel is from a new seller; each card carries the tag. */
  isNewSellerListing?: boolean
  /** Caps the cards shown, so the endpoint can over-fetch to survive the expiry filter. */
  maxItems?: number
  /** Puts the view-more link under the title, on every screen size, instead of beside it. */
  viewMoreUnderTitle?: boolean
  viewMoreLabel?: React.ReactNode
  /**
   * `'listing'` renders the all-auctions listing card, priced by its own
   * lookup, and shows the auction cards until that lookup succeeds. It ignores
   * `isNewSellerListing` and `carousel_position`.
   */
  cardStyle?: 'auction' | 'listing'
}

const TitleSkeleton = styled.div`
  width: 12.5rem;
  height: 1.5rem;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 0.25rem;

  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`

const ViewMoreSkeleton = styled(TitleSkeleton)`
  width: 6.25rem;
  height: 1.25rem;
`

export default function ShopMarketplaceCarousel({
  title,
  viewMoreUrl,
  endpoint,
  carousel_name,
  screenName,
  carousel_position,
  isNewSellerListing = false,
  maxItems,
  viewMoreUnderTitle,
  viewMoreLabel = <FormattedMessage id="Common.Carousel.viewAll" />,
  cardStyle = 'auction',
}: ShopMarketplaceCarouselProps) {
  const { data, isLoading } = useAuctionQuery(endpoint)
  const listings = useMemo(() => data?.slice(0, maxItems), [data, maxItems])
  const { track } = useAnalytics()
  const trackRef = useRef(track)
  useEffect(() => {
    trackRef.current = track
  })

  // Synchronous click handler always captures the latest track — trackRef is only needed for deferred calls (setTimeout)
  const handleViewMoreClick = () => {
    track('button_click', {
      button_name: 'carousel_view_more',
      carousel_name: carousel_name,
      carousel_position: carousel_position,
      screen_name: screenName ?? 'homepage',
      source: AnalyticsSource.HOME_PORTAL,
      entityType: AnalyticsEntityType.BUYER,
    })
  }

  const impressionTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const [currentImpressionId, setCurrentImpressionId] = useState<string | null>(
    null
  )
  const previousListingIdsRef = useRef<string[] | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  // GTM ecommerce tracking
  const itemListId = `carousel-${carousel_name ?? 'marketplace'}-homepage`
  const itemListName =
    typeof title === 'string' ? title : (carousel_name ?? 'marketplace')

  useViewItemListOnVisible({
    containerRef,
    listings: listings ?? [],
    itemListId,
    itemListName,
    enabled: !isLoading && (listings?.length ?? 0) > 0,
  })

  // Track impressions when listings load or when listing IDs/order changes
  useEffect(() => {
    if (!isLoading && listings?.length) {
      // Get current listing IDs in order
      const currentListingIds = listings.map((listing) => listing.id)

      // Check if this is first load or if IDs/order changed
      const isFirstLoad = previousListingIdsRef.current === null
      const idsChanged =
        previousListingIdsRef.current !== null &&
        (previousListingIdsRef.current.length !== currentListingIds.length ||
          previousListingIdsRef.current.some(
            (id, index) => id !== currentListingIds[index]
          ))

      // Only track if first load or IDs/order changed
      if (isFirstLoad || idsChanged) {
        // Clear any existing timeout
        if (impressionTimeoutRef.current) {
          clearTimeout(impressionTimeoutRef.current)
        }

        // Store current IDs for next comparison
        previousListingIdsRef.current = currentListingIds

        // Set new timeout for impression tracking
        impressionTimeoutRef.current = setTimeout(() => {
          if (listings.length) {
            const impressionId = crypto.randomUUID()
            setCurrentImpressionId(impressionId)

            trackRef.current('listing_card_impression', {
              listing_card_impression_id: impressionId,
              listings_count: listings.length,
              carousel_name: carousel_name,
              carousel_position: carousel_position,
              screen_name: screenName ?? 'homepage',
              listings: listings.map((listing) => ({
                id: listing.id,
                recommended_rank: listing.customRank,
                is_control: listing.randomSetLabel,
                fmv_rank: listing.percentFMV ?? null,
              })),
            })
          }
        }, 3000)
      }
    }

    return () => {
      if (impressionTimeoutRef.current) {
        clearTimeout(impressionTimeoutRef.current)
      }
    }
  }, [isLoading, listings, carousel_name, carousel_position, screenName])

  const skeletonCount = 10

  const analyticsData = useMemo(
    () => ({
      ...(currentImpressionId && {
        listing_card_impression_id: currentImpressionId,
      }),
    }),
    [currentImpressionId]
  )

  if (!isLoading && (!listings || listings.length === 0)) {
    return null
  }

  const auctionCards = isLoading
    ? Array(skeletonCount)
        .fill(null)
        .map((_, index) => (
          <AuctionCardSkeleton
            data-testid={`skeleton-${index + 1}`}
            key={`skeleton-${index + 1}`}
          />
        ))
    : listings?.map((listing, index) =>
        !!listing.auctionUrl && !!listing.storefrontId ? (
          <AuctionCard
            data-testid={`auction-card-${listing.listingId}`}
            key={listing.listingId}
            listing={listing}
            carousel_name={carousel_name}
            carousel_position={carousel_position}
            screenName={screenName}
            position={index + 1}
            isNewSeller={isNewSellerListing}
          />
        ) : null
      ) || []
  const auctionCarousel = (
    <Carousel disabled={isLoading}>{auctionCards}</Carousel>
  )

  return (
    <NestableAnalyticsProvider value={analyticsData}>
      <ItemListProvider itemListId={itemListId} itemListName={itemListName}>
        <Container ref={containerRef}>
          <Header>
            {isLoading ? (
              <TitleSkeleton data-testid="title-skeleton" />
            ) : (
              <Title>{title}</Title>
            )}
            {isLoading ? (
              viewMoreUrl && !viewMoreUnderTitle ? (
                <ViewMoreSkeleton data-testid="view-more-skeleton" />
              ) : null
            ) : viewMoreUrl && !viewMoreUnderTitle ? (
              <ViewAllButton
                size="large"
                appearance="tertiary"
                href={viewMoreUrl}
                onClick={handleViewMoreClick}
              >
                {viewMoreLabel}
              </ViewAllButton>
            ) : null}
          </Header>
          {viewMoreUrl && viewMoreUnderTitle && !isLoading && (
            <Subheader>
              <SubheaderLink href={viewMoreUrl} onClick={handleViewMoreClick}>
                {viewMoreLabel}
              </SubheaderLink>
            </Subheader>
          )}
          {cardStyle === 'listing' ? (
            <ListingCardCarousel
              listings={listings}
              carousel_name={carousel_name}
              screenName={screenName}
              fallback={auctionCarousel}
            />
          ) : (
            auctionCarousel
          )}
          {viewMoreUrl && !viewMoreUnderTitle && !isLoading && (
            <ViewAllButtonMobile
              size="large"
              appearance="tertiary"
              href={viewMoreUrl}
              onClick={handleViewMoreClick}
            >
              {viewMoreLabel}
            </ViewAllButtonMobile>
          )}
        </Container>
      </ItemListProvider>
    </NestableAnalyticsProvider>
  )
}
